import type { Project } from "../types";

/**
 * BizView er masterdata for prosjekter (docs/bizview-masterdata.md):
 * - bare prosjekter som finnes i BizView vises (nettside-prosjekter uten kobling skjules)
 * - navnet er BizView-navnet (PROJECT_DESCR)
 *
 * API-et gjør dette selv etter utrulling; normaliseringen her gjør at
 * frontenden oppfører seg likt også mot et eldre API.
 */
type MedBizview = {
  projectName: string;
  bizviewProsjektId?: string | null;
  bizviewProjectDescr?: string | null;
};

export function fraBizview<T extends MedBizview>(rader: T[]): T[] {
  return rader
    .filter((p) => p.bizviewProsjektId != null && p.bizviewProsjektId !== "")
    .map((p) => {
      const navn = p.bizviewProjectDescr?.trim();
      return navn ? { ...p, projectName: navn } : p;
    });
}

/** GET /api/projects, normalisert. `alle` tar med BizView-prosjekter uten portal-rad. */
export async function hentProsjekter(opts?: { alle?: boolean }): Promise<Project[]> {
  const r = await fetch(opts?.alle ? "/api/projects?scope=all" : "/api/projects");
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return fraBizview((await r.json()) as Project[]);
}

/**
 * Oppretter portal-raden for et BizView-prosjekt som ikke har en ennå (samme
 * POST /api/projects som «Nytt prosjekt»: kjørebok med milepæler og aktiviteter,
 * skriver bare til portal-skjemaet). Alle data på prosjektsiden (økonomi, kalkyle,
 * fremdrift, kjøpere …) kobles via BizView-ID-en på raden.
 * Returnerer portalens ProjectId. Er prosjektet allerede opprettet (409), brukes det.
 */
export async function opprettFraBizview(p: {
  bizviewProsjektId: string | null;
  projectManagerId?: string | null;
}): Promise<number> {
  if (!p.bizviewProsjektId) throw new Error("Prosjektet mangler BizView-ID");
  const post = (projectManagerId: string | null) =>
    fetch("/api/projects", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bizviewProsjektId: p.bizviewProsjektId, projectManagerId }),
    });

  let res = await post(p.projectManagerId ?? null);
  // Prosjektlederen fra BizView finnes ikke (aktiv) i portalen: opprett uten
  if (res.status === 400 && p.projectManagerId) res = await post(null);

  const body = (await res.json().catch(() => ({}))) as {
    projectId?: number;
    error?: string;
  };
  if (res.ok && body.projectId) return body.projectId;
  if (res.status === 409) {
    const id = Number(/ProjectId (\d+)/.exec(body.error ?? "")?.[1]);
    if (id > 0) return id;
  }
  throw new Error(body.error ?? `${res.status} ${res.statusText}`);
}
